/**
 * A célula "Documento" da tabela de peças (E90.F6, GUS-14).
 *
 * Mostra o que o documento É — o nome dado pelo perito ou o tipo identificado
 * pelo catálogo — com o arquivo recebido em legenda, e deixa o perito renomear
 * ali mesmo: "Nome do arquivo deixando editar" (rodada de validação de
 * 2026-09-13). O nome vai ao exame e ao relatório pela mesma regra da tela.
 *
 * A edição é local à célula; quem grava é a página (`onRenomear`), que relê a
 * listagem e devolve o erro por peça pelo mesmo canal do código declarado.
 */
import { useEffect, useRef, useState } from 'react';

import { Badge, Button, Input } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ExameEletronicoCcbData, TipoDaPeca } from '@/lib/exame_eletronico';

import { LeituraPorImagemBadge } from './LeituraPorImagem';
import { CompartilhadoBadge } from './CcbDaMesa';

const t = ptBR.exameEletronico;
const tp = ptBR.exameEletronico.tabelaDePecas;

/**
 * O tipo da peça ao lado do nome (E84.F1): deduzido do rótulo do hash que a
 * cobriu no comprovante ou do cabeçalho (E86). `outro` e nulo não mostram nada.
 */
function TipoDaPecaBadge({
  docId,
  tipo,
}: {
  docId: string;
  tipo: TipoDaPeca | null;
}): JSX.Element | null {
  if (tipo === null || tipo === 'outro') return null;
  const rotulo = tp.tipoDaPeca[tipo];
  return (
    <Badge
      variant="neutral"
      data-testid={`peca-tipo-${docId}`}
      data-tipo={tipo}
      aria-label={tp.tipoDaPecaAria.replace('{tipo}', rotulo)}
    >
      {rotulo}
    </Badge>
  );
}

export interface CelulaNomeDoDocumentoProps {
  peca: ExameEletronicoCcbData;
  titulo: string;
  salvando: boolean;
  onSelecionar: () => void;
  onRenomear: (nome: string | null) => void;
}

export function CelulaNomeDoDocumento({
  peca,
  titulo,
  salvando,
  onSelecionar,
  onRenomear,
}: CelulaNomeDoDocumentoProps): JSX.Element {
  const docId = peca.source_document_id;
  const [editando, setEditando] = useState(false);
  const [rascunho, setRascunho] = useState('');
  const [invalido, setInvalido] = useState(false);
  const campo = useRef<HTMLInputElement>(null);

  // O foco vai ao campo quando a edição abre — por efeito, e não por
  // `autoFocus`, que o lint de acessibilidade recusa (foco roubado no mount).
  useEffect(() => {
    if (editando) campo.current?.focus();
  }, [editando]);

  const abrir = (): void => {
    setRascunho(peca.nome_do_documento ?? '');
    setInvalido(false);
    setEditando(true);
  };
  const salvar = (): void => {
    const nome = rascunho.trim();
    if (nome.length > 120) {
      setInvalido(true);
      return;
    }
    setEditando(false);
    // Vazio apaga o nome editado: a linha volta ao tipo identificado ou ao arquivo.
    onRenomear(nome === '' ? null : nome);
  };

  if (editando) {
    return (
      // Editar não é navegar: o clique dentro da célula não troca a peça aberta.
      <div
        className="space-y-sc-1"
        onClick={(evento) => evento.stopPropagation()}
        role="presentation"
        data-testid={`peca-nome-editor-${docId}`}
      >
        <Input
          aria-label={`${tp.nomeDoDocumento} — ${titulo}`}
          className="w-full min-w-[12rem] text-body-sm"
          ref={campo}
          value={rascunho}
          maxLength={121}
          onChange={(evento) => {
            setRascunho(evento.target.value);
            setInvalido(false);
          }}
          onKeyDown={(evento) => {
            if (evento.key === 'Enter') salvar();
            if (evento.key === 'Escape') setEditando(false);
          }}
          data-testid={`peca-nome-${docId}`}
        />
        {invalido ? (
          <p className="text-caption text-danger-700" role="alert">
            {tp.nomeInvalido}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-sc-2">
          <Button
            type="button"
            variant="secondary"
            onClick={salvar}
            data-testid={`peca-nome-salvar-${docId}`}
          >
            {tp.salvarNome}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setEditando(false)}
            data-testid={`peca-nome-cancelar-${docId}`}
          >
            {tp.cancelarNome}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-sc-1">
      <div className="flex flex-wrap items-center gap-sc-2">
        <Button
          type="button"
          variant="ghost"
          className="px-0 text-left"
          onClick={onSelecionar}
          data-testid={`peca-abrir-${docId}`}
          aria-label={tp.abrirQuadro.replace('{peca}', titulo)}
        >
          {titulo}
        </Button>
        <TipoDaPecaBadge docId={docId} tipo={peca.tipo_da_peca ?? null} />
        <LeituraPorImagemBadge
          docId={docId}
          estado={peca.leitura_por_imagem}
          motivo={peca.leitura_por_imagem_motivo}
        />
        <CompartilhadoBadge docId={docId} ccbs={peca.compartilhada_com} />
      </div>
      <p className="text-caption text-fg-dim" data-testid={`peca-arquivo-${docId}`}>
        {tp.arquivoRecebido.replace('{arquivo}', peca.original_filename)}
      </p>
      <Button
        type="button"
        variant="ghost"
        className="px-0"
        disabled={salvando}
        onClick={(evento) => {
          evento.stopPropagation();
          abrir();
        }}
        data-testid={`peca-renomear-${docId}`}
        aria-label={tp.renomearAria.replace('{peca}', titulo)}
      >
        {salvando ? t.form.salvando : tp.renomear}
      </Button>
    </div>
  );
}
